import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';

import Grid from '@mui/material/Grid';
import ImageUploadBox from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/Inputs/ImageUploadBox';

import useImageUrlGenerator from '~/hooks/useImageUrlGenerator';

import { Company } from '~/services/CorporateEventsService';
import { uploadImage } from '~/services/ImageService';

export interface CreateCompanyParams {
  name: string;
  logoId?: string;
}

interface CompanyFormProps {
  company?: Company;
  onSubmit: (data: CreateCompanyParams) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

export default function CompanyForm({ company, onSubmit, onCancel, isSubmitting = false }: CompanyFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<CreateCompanyParams>({
    name: '',
    logoId: '',
  });
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const logoImageUrl = useImageUrlGenerator(formData.logoId || '');

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        logoId: company.logoId || '',
      });
    }
  }, [company]);

  const handleChange = (field: keyof CreateCompanyParams) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleImageUpload = useCallback(
    async (file: File) => {
      setIsUploadingImage(true);
      try {
        const response = await uploadImage(file);
        setFormData((prev) => ({ ...prev, logoId: response.public_id }));
        enqueueSnackbar('Image uploaded successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`, {
          variant: 'error',
        });
      } finally {
        setIsUploadingImage(false);
      }
    },
    [enqueueSnackbar],
  );

  const handleRemoveImage = useCallback(() => {
    setFormData((prev) => ({ ...prev, logoId: '' }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth label="Company Name" value={formData.name} onChange={handleChange('name')} required />
          </Grid>

          <Grid size={12}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Logo
              </Typography>
              <Box
                sx={{
                  '& .MuiPaper-root': {
                    maxHeight: 150,
                    overflow: 'hidden',
                  },
                  '& .MuiPaper-root img': {
                    maxHeight: 150,
                    maxWidth: '100%',
                    objectFit: 'contain',
                  },
                }}
              >
                <ImageUploadBox
                  id="company-logo-upload"
                  name="logoId"
                  label="logo"
                  imageUrl={logoImageUrl}
                  onUpload={handleImageUpload}
                  onRemove={handleRemoveImage}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box>
          <Stack direction="row" spacing={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || isUploadingImage}
              startIcon={isUploadingImage ? <CircularProgress size={20} /> : undefined}
            >
              {isUploadingImage ? 'Uploading image...' : isSubmitting ? 'Saving...' : 'Save'}
            </Button>
            {onCancel && (
              <Button type="button" variant="text" onClick={onCancel} disabled={isSubmitting || isUploadingImage}>
                Cancel
              </Button>
            )}
          </Stack>
        </Box>
      </Stack>
    </form>
  );
}
